import { Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from '@/pages/Auth/LoginPage';
import { DashboardPage } from '@/pages/Dashboard/DashboardPage';
import { TenantSelectorPage } from '@/pages/TenantSelector/TenantSelectorPage';
import { PlaceholderPage } from '@/pages/Placeholder/PlaceholderPage';
import { KanbanBoardPage } from '@/pages/Kanban/KanbanBoardPage';
import { CalendarPage } from '@/pages/Calendar/CalendarPage';
import { NotificationsPage } from '@/pages/Notifications/NotificationsPage';
import { NotificationSettingsPage } from '@/pages/Notifications/NotificationSettingsPage';
import { RULLoginPage } from '@/pages/Examples/Forms/RULLoginPage';
import { TextInputShowcasePage } from '@/pages/Examples/Forms/TextInputShowcasePage';
import { SelectionControlsShowcasePage } from '@/pages/Examples/Forms/SelectionControlsShowcasePage';
import { DateTimeShowcasePage } from '@/pages/Examples/Forms/DateTimeShowcasePage';
import { NumberShowcasePage } from '@/pages/Examples/Forms/NumberShowcasePage';
import { FileUploadShowcasePage } from '@/pages/Examples/Forms/FileUploadShowcasePage';
import { EventFormPage } from '@/pages/Examples/Forms/EventFormPage';
import { TicketsPage } from '@/pages/Tickets/TicketsPage';
import { BusinessPartnersPage } from '@/pages/BusinessPartners/BusinessPartnersPage';
import { LeadsPage } from '@/pages/Leads/LeadsPage';
import { ChatPage } from '@/pages/Chat/ChatPage';
import { GeneratorsMapPage } from '@/pages/PowerGenerators/GeneratorsMapPage';
import { GeneratorsTablePage } from '@/pages/PowerGenerators/GeneratorsTablePage';
import { MyTimesheetsPage } from '@/pages/Timesheets/MyTimesheetsPage';
import { GroupTimesheetsPage } from '@/pages/Timesheets/GroupTimesheetsPage';
import { ProjectsPage } from '@/pages/Timesheets/ProjectsPage';
import { VideoCallPage } from '@/pages/VideoCall/VideoCallPage';
import {
  GoldenWingsPage,
  GoldenWingsCalendarPage,
  ResourcesPage,
  AirplanesPage,
  AirportsPage,
  InstructorsPage,
  StudentsPage,
} from '@/pages/GoldenWings';
import { BellvileDentalCalendarPage } from '@/pages/BellvileDental';
import { ProtectedLayout } from '@/components/common/ProtectedLayout';
import { isTenantIdentifiable } from '@/utils/tenant';

function App() {
  // If tenant cannot be identified (accessing via localhost), show tenant selector
  if (!isTenantIdentifiable()) {
    return <TenantSelectorPage />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* All protected routes nested under ProtectedLayout */}
      <Route element={<ProtectedLayout />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route
          path="/settings/notifications"
          element={<NotificationSettingsPage />}
        />
        <Route path="/apps/business-partners" element={<PlaceholderPage />} />
        <Route
          path="/apps/business-partners/all"
          element={<BusinessPartnersPage />}
        />
        <Route path="/apps/calendar" element={<PlaceholderPage />} />
        <Route path="/apps/calendar/my" element={<CalendarPage />} />
        <Route path="/apps/calendar/team" element={<PlaceholderPage />} />
        <Route path="/apps/admin/users" element={<PlaceholderPage />} />
        <Route path="/apps/admin/settings" element={<PlaceholderPage />} />
        <Route path="/apps/tickets" element={<PlaceholderPage />} />
        <Route path="/apps/tickets/all" element={<TicketsPage />} />
        <Route
          path="/apps/tickets/boards/:slug"
          element={<KanbanBoardPage />}
        />
        <Route path="/apps/leads" element={<LeadsPage />} />
        <Route path="/apps/chat" element={<ChatPage />} />
        <Route path="/apps/chat/:chatId" element={<ChatPage />} />
        <Route path="/apps/video-call" element={<VideoCallPage />} />
        <Route
          path="/apps/timesheet"
          element={<Navigate to="/apps/timesheet/my" replace />}
        />
        <Route path="/apps/timesheet/projects" element={<ProjectsPage />} />
        <Route path="/apps/timesheet/my" element={<MyTimesheetsPage />} />
        <Route
          path="/apps/timesheet/summary"
          element={<GroupTimesheetsPage />}
        />
        <Route path="/apps/power_generators" element={<PlaceholderPage />} />
        <Route
          path="/apps/power_generators/map"
          element={<GeneratorsMapPage />}
        />
        <Route
          path="/apps/power_generators/table"
          element={<GeneratorsTablePage />}
        />
        <Route path="/apps/examples/forms/login" element={<RULLoginPage />} />
        <Route
          path="/apps/examples/forms/text-input-showcase"
          element={<TextInputShowcasePage />}
        />
        <Route
          path="/apps/examples/forms/selection-controls-showcase"
          element={<SelectionControlsShowcasePage />}
        />
        <Route
          path="/apps/examples/forms/datetime-showcase"
          element={<DateTimeShowcasePage />}
        />
        <Route
          path="/apps/examples/forms/number-showcase"
          element={<NumberShowcasePage />}
        />
        <Route
          path="/apps/examples/forms/file-upload-showcase"
          element={<FileUploadShowcasePage />}
        />
        <Route path="/apps/examples/forms/event" element={<EventFormPage />} />

        {/* GoldenWings Flight School */}
        <Route path="/apps/goldenwings" element={<GoldenWingsPage />} />
        <Route
          path="/apps/goldenwings/calendar"
          element={<GoldenWingsCalendarPage />}
        />
        <Route path="/apps/goldenwings/resources" element={<ResourcesPage />} />
        <Route
          path="/apps/goldenwings/resources/airplanes"
          element={<AirplanesPage />}
        />
        <Route
          path="/apps/goldenwings/resources/airports"
          element={<AirportsPage />}
        />
        <Route
          path="/apps/goldenwings/resources/instructors"
          element={<InstructorsPage />}
        />
        <Route
          path="/apps/goldenwings/resources/students"
          element={<StudentsPage />}
        />

        {/* Bellville Dental */}
        <Route
          path="/apps/bellviledental/calendar"
          element={<BellvileDentalCalendarPage />}
        />
      </Route>

      {/* Default redirect to dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App;
